import React from "react";
import {
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
} from "remotion";
import { theme } from "../../brand";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { FrakLogo } from "../../components/FrakLogo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { CARDS } from "./copy";

/**
 * Reel "Les cartes" (9:16, silent, ~14.7s): trading cards of everyday
 * influencers flip off a deck (la pote bon plan, l'oncle des avis, la
 * coloc skincare: a few hundred followers, a super-power, paid 0 €), each
 * flicked away. The last card is TOI, holographic: followers "peu
 * importe", paid on every sale through your link. End card: the landing's
 * "On est tous des influenceurs" / "Avec Frak, tu es payé pour le faire !".
 *
 * Frame 0 is the cover (hook + the deck, face down). Silent: the trending
 * sound is added in Instagram, so nothing is beat-locked.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** Each card flips face up on `flip[i]`. */
  flip: [10, 96, 168, 252],
  /** …and is flicked off the deck on `flick[i]` (not the last one). */
  flick: [84, 156, 228],
  /** Rows of a card appear from `flip + rows`, one every `rowStagger`. */
  rows: 12,
  rowStagger: 6,
  exit: 336,
  end: 348,
  duration: 440,
} as const;

export const CARDS_DURATION = T.duration;

if (T.flip.length !== CARDS.cards.length || T.flick.length !== T.flip.length - 1) {
  throw new Error("Cards reel: one flip per card, one flick per card but the last.");
}

const C = theme.brand.colors;
/** Frame and art tint per card, from the brand palette; the last one is foil. */
const PALETTE = [
  { frame: C.warning[400], tint: C.warning[100] },
  { frame: C.success[500], tint: C.success[100] },
  { frame: C.error[300], tint: C.error[100] },
  {
    frame: `linear-gradient(135deg, ${C.primary[300]}, ${C.success[300]}, ${C.warning[300]}, ${C.primary[300]})`,
    tint: LOOK.sky,
  },
];
const GREEN = C.success[800];

/** Card geometry (logical px). Centred, clear of the Reels rail on the right. */
const W = 700;
const H = 900;
const TOP = 500;
const BORDER = 16;
/** Resting tilt of a card still in the deck. */
const REST = [0, 4, -3.5, 3];

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number) =>
  interpolate(frame, [at, at + len], [0, 1], {
    ...clamp,
    easing: Easing.out(Easing.cubic),
  });

const Row: React.FC<{
  label: string;
  value: string;
  at: number;
  big?: boolean;
  color?: string;
}> = ({ label, value, at, big, color = LOOK.ink }) => {
  const frame = useCurrentFrame();
  const p = spring({
    frame: frame - at,
    fps: REEL.fps,
    config: { damping: 16, stiffness: 200, mass: 0.6 },
  });
  return (
    <div
      style={{
        opacity: Math.min(1, p * 1.5),
        translate: `0 ${(1 - p) * 24}px`,
        borderTop: `2px solid ${LOOK.hairline}`,
        paddingTop: 14,
      }}
    >
      <div
        style={{
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: 26,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: LOOK.muted,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: big ? LOOK.bold : LOOK.semi,
          fontSize: big ? 54 : 40,
          letterSpacing: -0.8,
          lineHeight: 1.15,
          color,
          marginTop: 4,
          textWrap: "balance",
        }}
      >
        {value}
      </div>
    </div>
  );
};

const Front: React.FC<{ i: number }> = ({ i }) => {
  const frame = useCurrentFrame();
  const card = CARDS.cards[i];
  const { frame: edge, tint } = PALETTE[i];
  const you = i === CARDS.cards.length - 1;
  const rowsAt = T.flip[i] + T.rows;

  // Holographic band sweeping across the last card (brand tints only).
  const sweep = interpolate(frame, [T.flip[i] + 10, T.flip[i] + 70], [-60, 160], clamp);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backfaceVisibility: "hidden",
        borderRadius: 40,
        background: edge,
        padding: BORDER,
      }}
    >
      <div
        style={{
          position: "relative",
          height: "100%",
          borderRadius: 26,
          background: LOOK.white,
          padding: "26px 30px 30px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span
            style={{
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
              fontSize: you ? 76 : 50,
              letterSpacing: you ? 2 : -0.5,
              textTransform: "uppercase",
              color: LOOK.ink,
            }}
          >
            {card.name}
          </span>
          {you ? (
            <FrakLogo width={170} />
          ) : (
            <span
              style={{
                fontFamily: LOOK.body,
                fontWeight: LOOK.semi,
                fontSize: 26,
                color: LOOK.muted,
              }}
            >
              {i + 1}/{CARDS.cards.length}
            </span>
          )}
        </div>

        <div
          style={{
            height: 260,
            flexShrink: 0,
            borderRadius: 22,
            background: tint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 170,
            lineHeight: 1,
          }}
        >
          {card.art}
        </div>

        <Row label={CARDS.labels.followers} value={card.followers} at={rowsAt} />
        <Row
          label={CARDS.labels.power}
          value={card.power}
          at={rowsAt + T.rowStagger}
        />
        <Row
          label={CARDS.labels.pay}
          value={card.pay}
          at={rowsAt + 2 * T.rowStagger}
          big
          color={you ? GREEN : LOOK.ink}
        />

        {you ? (
          <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                mixBlendMode: "multiply",
                opacity: 0.7,
                background: `linear-gradient(115deg, transparent ${sweep - 30}%, ${C.primary[300]} ${sweep - 14}%, ${C.success[300]} ${sweep}%, ${C.warning[300]} ${sweep + 14}%, transparent ${sweep + 30}%)`,
              }}
          />
        ) : null}
      </div>
    </div>
  );
};

/** Card back: white, Frak blue frame, the Frak mark. */
const Back: React.FC = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backfaceVisibility: "hidden",
      rotate: "y 180deg",
      borderRadius: 40,
      background: LOOK.white,
      border: `${BORDER}px solid ${LOOK.blue}`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 40,
    }}
  >
    <FrakLogo variant="icon" width={260} color={LOOK.blue} />
    <FrakLogo width={220} />
  </div>
);

const Card: React.FC<{ i: number }> = ({ i }) => {
  const frame = useCurrentFrame();
  const last = i === CARDS.cards.length - 1;

  // Depth in the deck: cards above it still there, eased as they leave.
  const depth =
    i - T.flick.slice(0, i).reduce((d, at) => d + ramp(frame, at + 4, 10), 0);
  const up = Math.min(depth, 1);

  const flip = spring({
    frame: frame - T.flip[i],
    fps: REEL.fps,
    config: { damping: 13, stiffness: 120, mass: 0.8 },
  });

  const flickAt = T.flick[i];
  const flick =
    flickAt === undefined
      ? 0
      : interpolate(frame, [flickAt, flickAt + 14], [0, 1], {
          ...clamp,
          easing: Easing.in(Easing.cubic),
        });

  // The last card tilts gently so its holo band reads as light on foil.
  const tilt = last && frame >= T.flip[i] + 14 ? 7 * Math.sin((frame - T.flip[i]) / 14) : 0;

  const leave = last
    ? interpolate(frame, [T.exit, T.exit + 14], [0, 1], {
        ...clamp,
        easing: Easing.in(Easing.cubic),
      })
    : 0;

  if (flick >= 1 || leave >= 1) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: (REEL.width - W) / 2 - 20,
        top: TOP,
        width: W,
        height: H,
        zIndex: CARDS.cards.length - i,
        // The last card drops out (never through the hook above it).
        translate: `${-flick * 1300}px ${depth * 22 + leave * 1500}px`,
        rotate: `${REST[i] * up - flick * 28}deg`,
        scale: (1 - 0.03 * depth) * (1 + 0.06 * Math.sin(Math.PI * Math.min(flip, 1))),
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 40,
          boxShadow: FLOAT_SHADOW,
          transformStyle: "preserve-3d",
          transform: `perspective(2200px) rotateY(${180 * (1 - flip) + tilt}deg)`,
        }}
      >
        <Front i={i} />
        <Back />
      </div>
    </div>
  );
};

export const CardsReel: React.FC = () => (
  <ReelFrame
    hook={CARDS.hook}
    exitAt={T.exit}
    end={{ at: T.end, line: CARDS.end.line, sub: CARDS.end.sub }}
  >
    {CARDS.cards.map((card, i) => (
      <Card key={card.name} i={i} />
    ))}
  </ReelFrame>
);
